/**
 * Standalone Discord profile sync. Fetches the same Lanyard widget proxy
 * response that the on-card Lanyard widget uses, and dispatches a
 * `bio:discord-profile` CustomEvent with the same payload. Other
 * components (avatar sync, favicon sync) listen for that event and apply
 * Discord data — they don't care whether it came from the widget or this
 * standalone fetcher.
 *
 * This decouples Discord-driven features (avatar, Nitro decoration,
 * nameplate, favicon) from having a Lanyard widget TILE on the card.
 * Users who want the avatar sync but don't want the widget displayed can
 * just set `config.discord.userId` in admin.
 *
 * Re-fetches every 30s to match the Lanyard widget's cadence. When the
 * Lanyard widget IS also mounted for the same user, both will fire — no
 * conflict because the listeners are idempotent (they no-op if the
 * incoming value matches the current DOM state).
 */

const REFRESH_MS = 30_000;

export function startDiscordSync(userId: string): () => void {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const fetchOnce = async () => {
    if (stopped) return;
    try {
      const r = await fetch(
        `/bio-api/widgets/lanyard?uid=${encodeURIComponent(userId)}`,
        { cache: "no-store", signal: controller.signal },
      );
      if (!r.ok) throw new Error(`lanyard ${r.status}`);
      const env = (await r.json()) as { ok: boolean; data?: unknown };
      if (!env.ok || !env.data) throw new Error("lanyard returned no data");
      // Re-broadcast on the same channel the widget uses so avatar.ts /
      // main.ts favicon sync / anything listening updates uniformly.
      const d = env.data as {
        userId: string;
        avatarUrl: string | null;
        decorationUrl: string | null;
        bannerUrl: string | null;
        globalName: string | null;
        username: string;
        nameplate: unknown;
        nameStyle: unknown;
      };
      document.dispatchEvent(
        new CustomEvent("bio:discord-profile", {
          detail: {
            userId: d.userId,
            avatarUrl: d.avatarUrl,
            decorationUrl: d.decorationUrl,
            bannerUrl: d.bannerUrl,
            globalName: d.globalName,
            username: d.username,
            nameplate: d.nameplate,
            nameStyle: d.nameStyle,
          },
        }),
      );
    } catch (err) {
      // Silent — standalone sync is best-effort. If the widget is ALSO
      // mounted its own fetch may succeed; if neither works, avatar
      // falls back to the configured static src as designed.
      if ((err as Error).name !== "AbortError") {
        console.warn("[discord-sync] fetch failed", err);
      }
    } finally {
      if (!stopped) {
        timer = setTimeout(fetchOnce, REFRESH_MS);
      }
    }
  };

  void fetchOnce();

  return () => {
    stopped = true;
    controller.abort();
    if (timer) clearTimeout(timer);
  };
}
